// File: src/components/forms/CreditCardField/CreditCardField.tsx
// Purpose: Form input component

import React, { useState } from "react";
import { CreditCard } from "lucide-react";
import { TextField, TextFieldProps } from "../TextField";

/** 📌 Detects card type based on first digits */
const detectCardType = (number: string) => {
  const cleaned = number.replace(/\D/g, ""); // Remove non-numeric characters

  const patterns: { [key: string]: RegExp } = {
    visa: /^4/,
    mastercard: /^5[1-5]/,
    amex: /^3[47]/,
    discover: /^6(?:011|5)/,
  };

  for (const [type, regex] of Object.entries(patterns)) {
    if (regex.test(cleaned)) return type;
  }

  return null;
};

/** 📌 Formats credit card numbers dynamically */
const formatCreditCardNumber = (value: string) => {
  const cleaned = value.replace(/\D/g, "").slice(0, 16); // ✅ Enforce max length 16 digits
  const cardType = detectCardType(cleaned);
  let formatted = "";

  if (cardType === "amex") {
    // AMEX format: 4-6-5 (15 digits)
    formatted = cleaned.replace(/(\d{4})(\d{6})?(\d{5})?/, (_, g1, g2, g3) => {
      return [g1, g2, g3].filter(Boolean).join(" ");
    });
  } else {
    // Default: Visa, MasterCard, Discover (16 digits)
    formatted = cleaned.replace(/(\d{4})(\d{4})?(\d{4})?(\d{4})?/, (_, g1, g2, g3, g4) => {
      return [g1, g2, g3, g4].filter(Boolean).join(" ");
    });
  }

  return formatted;
};

/** 📌 Validates credit card using Luhn Algorithm */
const validateCreditCard = (number: string) => {
  const cleaned = number.replace(/\D/g, ""); // Strip non-numeric chars

  if (cleaned.length === 0) return null; // ✅ No error when empty
  if (cleaned.length < 13) return "Credit card must be 13+ digits"; // ⚠️ Show error if too short
  if (!luhnCheck(cleaned)) return "Invalid credit card number"; // 🚨 Luhn check failed
  return null; // ✅ Valid credit card
};

/** 📌 Implements Luhn Algorithm for card validation */
const luhnCheck = (cardNumber: string) => {
  let sum = 0;
  let alternate = false;

  for (let i = cardNumber.length - 1; i >= 0; i--) {
    let n = parseInt(cardNumber[i], 10);
    if (alternate) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alternate = !alternate;
  }

  return sum % 10 === 0;
};

export const CreditCardField: React.FC<Omit<TextFieldProps, "type" | "maxLength">> = (props) => {
  const [rawValue, setRawValue] = useState<string>(""); // Stores raw numeric value
  const [error, setError] = useState<string | null>(props.error ?? null);
  const [touched, setTouched] = useState(false); // Tracks focus to delay validation

  return (
    <TextField
      {...props}
      type="text"
      icon={CreditCard}
      placeholder="1234 5678 9012 3456"
      value={formatCreditCardNumber(rawValue)} // ✅ Always display formatted value
      maxLength={19} // ✅ Allows space formatting (16 digits + 3 spaces)
      onChange={(e) => {
        let cleaned = e.target.value.replace(/\D/g, ""); // ✅ Remove non-numeric characters

        if (cleaned.length <= 16) {
          setRawValue(cleaned); // ✅ Restrict max length
        }

        if (touched) {
          setError(validateCreditCard(cleaned)); // ✅ Validate in real-time after blur
        }
      }}
      onBlur={() => {
        setTouched(true);
        setError(validateCreditCard(rawValue)); // ✅ Show error after blur
      }}
      // error={touched ? error : undefined} // ✅ Show error only after blur
      error={
        props.error !== undefined && props.error !== null
          ? props.error
          : touched
          ? error
          : null
      }
    />
  );
};